import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useQuery, useMutation } from 'components/graphql'
import { listOrderedItems } from '../../gql'
import { generatePdfOrder } from 'sections/statistics/gql/generatePdfOrder'
import DxDataTable from 'components/DxDataTable'
import { getMaxTableHeight } from '../helpers'
import ContenHeader from 'components/ContenHeader'
import { Modal } from 'components/Modal'
import ViewerPDF from 'components/ViewerPDF'
import Divider from 'components/Divider'
import Button from 'components/Button'
import { dxOrderItemsCols } from './columns'
import moment from 'moment'
import { DataWrapper, DataRow } from './styled'

const dataMap = t => [
	[{ key: t('common.strings.branch') }, { key: t('statistic_section.strings.created') }],
	[{ key: t('common.fields.cash_register') }, { key: t('statistic_section.strings.delivery_note_num') }],
	[{ key: t('statistic_section.strings.employee') }, { key: t('common.strings.customer') }],
	[{ key: t('statistic_section.strings.registration_date') }, { key: t('statistic_section.strings.delivery_note_num_fa') }]
]

const OrderItems = ({ orderData, initialSelectedOrder }) => {
	const { data, queryField } = useQuery(listOrderedItems, {
		variables: { id: orderData?.id },
		fetchPolicy: 'cache-first'
	})
	const [pdfUrl, setPdfUrl] = useState('')
	const [openModal, setOpenModal] = useState(false)
	const [generateOrder] = useMutation(generatePdfOrder, {
		onCompleted: res => {
			const url = res?.generatePdfOrder?.url
			if(url) {
				setPdfUrl(url)
				setOpenModal((prev) => !prev)
			}
		}
	})

	const [t] = useTranslation()
	const getTableHeight = useCallback(() => getMaxTableHeight('orderItemsTable', 200))
	const tableCols = dxOrderItemsCols(t)

	const getRows = arr => {
		return arr.map(([col1, col2], i) => (
			<DataWrapper key={i}>
				<DataRow>
					<span>{col1.label || col1.key}</span>
					<span>{orderData?.[col1.key]}</span>
				</DataRow>
				<DataRow>
					<span>{col2.label || col2.key}</span>
					<span>{orderData?.[col2.key]}</span>
				</DataRow>
			</DataWrapper>
		))
	}

	const { cashRegisterName, creditClientName, userName, dateOrder, dateSend, dateReceive, importNumber, shopName, deliveryNoteNumber } = initialSelectedOrder
	const formatDateOrder = moment(dateOrder).format('DD/MM/YYYY')
	const formatCreated = moment(dateSend).utc().format('DD.MM.YYYY, hh:mm:ss, a')
	const formatReceived = dateReceive ? moment(dateReceive).utc().format('DD.MM.YYYY, hh:mm:ss, a') : ''
	const tableData = data?.[queryField]?.map(item => [item?.product?.code, item?.amount, item?.product?.name])
	const orderJSON = [
		{
			message: `${formatDateOrder} ${t('statistic_section.strings.order')}`,
			textSize: 'TEXT_XXL',
			messageUnderline: 'UNDERLINE',
			horizontalAlign: 'CENTER'
		},
		{
			message: `${t('statistic_section.strings.order')}:`,
			textSize: 'TEXT_XL',
			horizontalAlign: 'LEFT'
		},
		{
			items: [
				{
					width: 100,
					message: `${t('common.strings.branch')}: ${shopName || ''}`,
					marginElement: [10, 0, 0, 0]
				},
				{
					width: 100,
					message: `${t('statistic_section.strings.created')}: ${formatCreated}`
				}
			]
		},
		{
			items: [
				{
					width: 100,
					message: `${t('common.fields.cash_register')}: ${cashRegisterName || ''}`,
					marginElement: [10, 0, 0, 0]
				},
				{
					width: 100,
					message: `${t('statistic_section.strings.delivery_note_num')}: ${deliveryNoteNumber || ''}`
				}
			]
		},
		{
			items: [
				{
					width: 100,
					message: `${t('statistic_section.strings.employee')}: ${userName || ''}`,
					marginElement: [10, 0, 0, 0]
				},
				{
					width: 100,
					message: `${t('common.strings.customer')}: ${creditClientName || ''}`
				}
			]
		},
		{
			items: [
				{
					width: 100,
					message: `${t('statistic_section.strings.registration_date')}: ${formatReceived}`,
					marginElement: [10, 0, 0, 20]
				},
				{
					width: 100,
					message: `${t('statistic_section.strings.delivery_note_num_fa')}: ${importNumber || ''}`
				}
			]
		},
		{
			tableData: {
				widths: [70, 70, 200],
				body: [
					[t('common.fields.code'), t('credit_client_section.strings.amount'), t('common.fields.name')],
					...(tableData?.length ? tableData : [])
				]
			}
		}
	]

	const handleGeneratePdf = () => {
		generateOrder({
			variables: {
				orderJson: orderJSON
			}
		})
	}

	if(openModal) {
		return (
			<Modal unmount={() => setOpenModal(false)} fullScreen={true}>
			 	<ViewerPDF src={pdfUrl} style={{ height: '100%' }} />
		 	</Modal>
		)
	}

	return (
		<div>
			<ContenHeader /> {/* Empty ContenHeader for rows vertical align with orders Table */}
			<div style={{ textAlign: 'right', marginBottom: '10px' }}>
				<Button text={t('options_section.strings.printing')} type='button' onClick={handleGeneratePdf} />
			</div>
			<div>{getRows(dataMap(t))}</div>
			<Divider height={10} />
			<DxDataTable
				id='orderItemsTable'
				data={data?.[queryField] || []}
				columns={tableCols}
				height={getTableHeight}
				emptyNoDataText
				columnAutoWidth={false}
				withSearch={false}
			/>
		</div>
	)
}

export default OrderItems
